import { Dialog, PlainButton } from "@jugl-web/ui-kit";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as ActionsIcon } from "./assets/actions.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as SquareIcon } from "./assets/square.svg";
import { ReactComponent as CalendlyIcon } from "./assets/calendly.svg";
import { ReactComponent as CalcomIcon } from "./assets/calcom.svg";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import iphoneImage from "./assets/iphone.png";
import samsungImage from "./assets/samsung.png";
import sonyImage from "./assets/sony.png";
import pastaImage from "./assets/pasta.png";
import cakeImage from "./assets/cake.png";
import steakImage from "./assets/steak.png";
import dateImage from "./assets/date.png";
import hourImage from "./assets/hour.png";

export type IntegrationHowItWorksKind =
  | "shopify"
  | "squareup"
  | "calendly"
  | "cal";

type HowItWorksFeature = {
  id: string;
  title: string;
  description: string;
};

type HowItWorksCard = {
  id: string;
  imageSrc: string;
  title: string;
  price: string;
};

type HowItWorksViewModel = {
  brandName: string;
  brandIcon: React.ReactNode;
  features: HowItWorksFeature[];
  cardsExample?: {
    sectionLabel: string;
    cards: HowItWorksCard[];
  };
  calendarExample?: boolean;
};

const SQUARE_DEMO_CARDS: HowItWorksCard[] = [
  {
    id: "pasta",
    imageSrc: pastaImage,
    title: "Truffle Cream Pasta",
    price: "$18",
  },
  {
    id: "steak",
    imageSrc: steakImage,
    title: "Grilled Ribeye Steak",
    price: "$32",
  },
  {
    id: "cake",
    imageSrc: cakeImage,
    title: "Chocolate Lava Cake",
    price: "$9.50",
  },
];

const SHOPIFY_DEMO_CARDS: HowItWorksCard[] = [
  {
    id: "iphone",
    imageSrc: iphoneImage,
    title: "iPhone 14 Pro Max - 512GB, Deep Purple",
    price: "$1,199.00",
  },
  {
    id: "samsung",
    imageSrc: samsungImage,
    title: "Samsung Galaxy Z Fold4 - 256GB, Phantom Black",
    price: "$1,799.99",
  },
  {
    id: "sony",
    imageSrc: sonyImage,
    title: "Sony WH-1000XM5 Wireless Noise Cancelling Headphones",
    price: "$399.99",
  },
];

export const IntegrationsHowItWorksDialog: React.FC<{
  isOpen: boolean;
  kind: IntegrationHowItWorksKind | null;
  onRequestClose: () => void;
}> = ({ kind, isOpen, onRequestClose }) => {
  const { t } = useTranslations();
  const [insideKind, setInsideKind] =
    useState<IntegrationHowItWorksKind>("shopify");

  useEffect(() => {
    if (isOpen && kind) {
      setInsideKind(kind);
    }
  }, [isOpen, kind]);

  const viewModel = useMemo((): HowItWorksViewModel | null => {
    switch (insideKind) {
      case "shopify":
        return {
          brandName: t({
            id: "cx-actions-page.shopify",
            defaultMessage: "Shopify",
          }),
          brandIcon: <ShopifyIcon />,
          features: [
            {
              id: "products",
              title: t({
                id: "cx-actions-page.shopify-show-products",
                defaultMessage: "Show products",
              }),
              description: t({
                id: "cx-actions-page.shopify-show-products-description",
                defaultMessage:
                  "Show available products from your Shopify Account in the conversation",
              }),
            },
            {
              id: "cart",
              title: t({
                id: "cx-actions-page.shopify-show-cart",
                defaultMessage: "Show what's in cart",
              }),
              description: t({
                id: "cx-actions-page.shopify-show-cart-description",
                defaultMessage:
                  "When a user asks what's in their cart, show the details of items added to the cart.",
              }),
            },
            {
              id: "order",
              title: t({
                id: "cx-actions-page.shopify-show-order-status",
                defaultMessage: "Show order status",
              }),
              description: t({
                id: "cx-actions-page.shopify-show-order-status-description",
                defaultMessage: "Show order status with all collected details.",
              }),
            },
            {
              id: "profile",
              title: t({
                id: "cx-actions-page.shopify-update-profile",
                defaultMessage: "Update profile",
              }),
              description: t({
                id: "cx-actions-page.shopify-update-profile-description",
                defaultMessage:
                  "Update the profile attributes of the customer account in Shopify.",
              }),
            },
            {
              id: "billing",
              title: t({
                id: "cx-actions-page.shopify-update-billing-address",
                defaultMessage: "Update billing address",
              }),
              description: t({
                id: "cx-actions-page.shopify-update-billing-address-description",
                defaultMessage:
                  "Update the billing address of the customer account in Shopify.",
              }),
            },
          ],
          cardsExample: {
            sectionLabel: t({
              id: "cx-actions-page.cards-example",
              defaultMessage: "✨ Cards Example:",
            }),
            cards: SHOPIFY_DEMO_CARDS,
          },
        };

      case "squareup":
        return {
          brandName: t({
            id: "cx-actions-page.square",
            defaultMessage: "Square",
          }),
          brandIcon: <SquareIcon />,
          features: [
            {
              id: "products",
              title: t({
                id: "cx-actions-page.square-show-products",
                defaultMessage: "Show products",
              }),
              description: t({
                id: "cx-actions-page.square-show-products-description",
                defaultMessage:
                  "Show available products from your Square account in the conversation.",
              }),
            },
            {
              id: "cart",
              title: t({
                id: "cx-actions-page.square-show-cart",
                defaultMessage: "Show what's in cart",
              }),
              description: t({
                id: "cx-actions-page.square-show-cart-description",
                defaultMessage:
                  "When a user asks what's in their cart, show the details of items added to the cart.",
              }),
            },
            {
              id: "order",
              title: t({
                id: "cx-actions-page.square-show-order-status",
                defaultMessage: "Show order status",
              }),
              description: t({
                id: "cx-actions-page.square-show-order-status-description",
                defaultMessage: "Show order status with all collected details.",
              }),
            },
          ],
          cardsExample: {
            sectionLabel: t({
              id: "cx-actions-page.cards-example",
              defaultMessage: "✨ Cards Example:",
            }),
            cards: SQUARE_DEMO_CARDS,
          },
        };

      case "calendly":
        return {
          brandName: t({
            id: "cx-actions-page.calendly",
            defaultMessage: "Calendly",
          }),
          brandIcon: <CalendlyIcon />,
          features: [
            {
              id: "slots",
              title: t({
                id: "cx-actions-page.calendly-show-available-slots",
                defaultMessage: "Show available time slots",
              }),
              description: t({
                id: "cx-actions-page.calendly-show-available-slots-description",
                defaultMessage:
                  "When a user asks to book a meeting, show the calendar with available time slots retrieved from your Calendly account.",
              }),
            },
          ],
          calendarExample: true,
        };

      case "cal":
        return {
          brandName: t({
            id: "cx-actions-page.calcom",
            defaultMessage: "Cal.com",
          }),
          brandIcon: <CalcomIcon />,
          features: [
            {
              id: "slots",
              title: t({
                id: "cx-actions-page.calcom-show-available-slots",
                defaultMessage: "Show available time slots",
              }),
              description: t({
                id: "cx-actions-page.calcom-show-available-slots-description",
                defaultMessage:
                  "When a user asks to book a meeting, show available times from your Cal.com scheduling links.",
              }),
            },
          ],
          calendarExample: true,
        };

      default: {
        // Compile-time exhaustiveness check: this assignment fails type-check
        // if a new `IntegrationHowItWorksKind` is added without a case above.
        // At runtime (if the union is ever widened via `as` casts), we render
        // nothing rather than crashing during render.
        const exhaustiveCheck: never = insideKind;
        return exhaustiveCheck;
      }
    }
  }, [insideKind, t]);

  if (!viewModel) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className="flex w-full max-w-[780px] flex-col"
    >
      <div className="flex h-[60px] w-full items-center justify-between border-0 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-3">
          <ActionsIcon />
          <div className="font-secondary font-semibold text-[#383838]">
            {t({
              id: "cx-actions-page.how-it-works",
              defaultMessage: "How it works",
            })}
          </div>
        </div>
        <PlainButton
          onClick={onRequestClose}
          className="flex items-center justify-center"
        >
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar flex flex-col overflow-y-auto px-14 py-10">
        <div className="flex items-center gap-2">
          {viewModel.brandIcon}
          <span className="font-secondary text-[22px] font-semibold text-[#1A2023]">
            {viewModel.brandName}
          </span>
        </div>
        <div className="mt-6 flex flex-col">
          {viewModel.features.map((action, index) => (
            <React.Fragment key={action.id}>
              <div className="flex flex-col gap-1 py-2">
                <span className="font-secondary text-[15px] font-medium text-dark">
                  {action.title}
                </span>
                <span className="font-secondary text-sm leading-[160%] text-dark-700">
                  {action.description}
                </span>
              </div>
              {index < viewModel.features.length - 1 && (
                <div className="my-2.5 h-px w-full rounded-[1px] bg-[#F2F2F2]" />
              )}
            </React.Fragment>
          ))}
        </div>
        {viewModel.cardsExample && (
          <div className="mt-10 flex flex-col gap-2">
            <span className="font-secondary text-sm text-dark-600">
              {viewModel.cardsExample.sectionLabel}
            </span>
            <div className="flex items-start gap-4">
              {viewModel.cardsExample.cards.map((card) => (
                <div
                  key={card.id}
                  className="min-w-0 flex-1 basis-0 overflow-hidden rounded-[14px] border border-solid border-dark-200"
                >
                  <img
                    src={card.imageSrc}
                    alt={card.title}
                    className="h-[130px] w-full shrink-0 border-0 border-b border-solid border-dark-200 object-cover"
                  />
                  <div className="flex flex-col px-2.5 py-[13px]">
                    <span className="font-secondary text-xs leading-2 font-medium text-dark">
                      {card.title}
                    </span>
                    <div className="mt-1.5 flex items-center gap-1">
                      <span className="font-secondary text-[13px] font-semibold text-dark">
                        {card.price}
                      </span>
                      <span className="font-secondary text-[10px] text-grey-900">
                        /unit
                      </span>
                    </div>
                    <div className="mt-6 flex items-center gap-2">
                      <div className="flex h-7 w-[50px] cursor-pointer items-center justify-center rounded-lg bg-grey-100 font-secondary text-xs text-dark-800">
                        {t({
                          id: "common.info",
                          defaultMessage: "Info",
                        })}
                      </div>
                      <div className="flex h-7 w-full cursor-pointer items-center justify-center gap-1 rounded-lg bg-dark font-secondary text-xs text-white">
                        <span>
                          {t({
                            id: "common.select",
                            defaultMessage: "Select",
                          })}
                        </span>
                        <ArrowIcon />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {viewModel.calendarExample && (
          <div className="mt-10 flex flex-col gap-2">
            <span className="font-secondary text-sm text-dark-600">
              {t({
                id: "cx-actions-page.calendar-example",
                defaultMessage: "✨ Calendar Example:",
              })}
            </span>
            <div className="flex flex-col gap-4">
              <div className="flex max-w-full items-start gap-4">
                <img src={dateImage} className="h-[284px] max-w-full" alt="" />
                <img src={hourImage} className="h-[284px] max-w-full" alt="" />
              </div>
              <div className="flex w-[272px] flex-col overflow-hidden rounded-2xl border border-solid border-dark-200 bg-white py-6">
                <div className="flex flex-col gap-3 px-6">
                  <div className="flex items-center gap-2">
                    <CalendarIcon />
                    <span className="font-secondary text-[15px] leading-[20px] font-medium text-dark">
                      {t({
                        id: "cx-actions-page.calendar-example-booking-details",
                        defaultMessage: "Booking details",
                      })}
                    </span>
                  </div>
                  <span className="font-sm text-sm leading-[140%] text-dark-700">
                    {t({
                      id: "cx-actions-page.calendar-example-booking-body",
                      defaultMessage:
                        "You attempted to book a slot at 14:00 on Thursday, April 22, 2026",
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
